import React, { useEffect, useState } from "react";
import Button from "./Button";
import {API_BASE} from "../../config";
import { useNavigate } from "react-router-dom";

// Helper: Extracts plain text from a JSX element
const getOfferText = (offer) => {
  if (typeof offer === "string") return offer;
  if (React.isValidElement(offer)) {
    let text = "";
    React.Children.forEach(offer.props.children, (child) => {
      if (typeof child === "string") {
        text += child;
      } else if (React.isValidElement(child) && child.props.children) {
        text += child.props.children;
      }
    });
    return text.trim();
  }
  return "";
};

const fetchPincodeDetails = async (pincode) => {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  const data = await res.json();
  if (data[0].Status === "Success") {
    const postOffice = data[0].PostOffice[0];
    return { city: postOffice.District, state: postOffice.State };
  } else {
    throw new Error("Invalid Pincode");
  }
};

const DetailsModal = ({ isOpen, onClose, onSubmit }) => {
  const initialFormData = {
    name: "",
    mobile: "",
    email: "",
    pincode: "",
    city: "",
    state: "",
    address: ""
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (!isOpen) setFormData(initialFormData);
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const pincode = formData.pincode.trim();
    if (pincode.length === 6) {
      fetchPincodeDetails(pincode)
        .then((result) => {
          setFormData((prev) => ({
            ...prev,
            city: result.city,
            state: result.state
          }));
        })
        .catch(() => {
          setFormData((prev) => ({ ...prev, city: "", state: "" }));
          alert("Invalid Pincode. Please check and try again.");
        });
    }
  }, [formData.pincode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 w-full h-full px-4">
      <div className="relative bg-orange-500 rounded-lg shadow-lg w-full max-w-md p-6 overflow-auto">
        <button onClick={onClose} className="absolute top-2 right-2 text-white text-2xl font-bold">
          &times;
        </button>
        <h2 className="text-center text-white text-xl font-semibold mb-4">Provide Details</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-white text-sm mb-1" htmlFor="name">Name</label>
            <input
              className="w-full px-3 py-2 rounded"
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          {/* Mobile */}
          <div>
            <label className="block text-white text-sm mb-1" htmlFor="mobile">Mobile</label>
            <input
              className="w-full px-3 py-2 rounded"
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </div>
          {/* Email */}
          <div>
            <label className="block text-white text-sm mb-1" htmlFor="email">Email</label>
            <input
              className="w-full px-3 py-2 rounded"
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          {/* Pincode */}
          <div>
            <label className="block text-white text-sm mb-1" htmlFor="pincode">Pincode</label>
            <input
              className="w-full px-3 py-2 rounded"
              type="text"
              id="pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              required
            />
          </div>
          {/* City & State */}
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <div className="w-full sm:w-1/2">
              <label className="block text-white text-sm mb-1" htmlFor="city">City</label>
              <input
                className="w-full px-3 py-2 rounded"
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="w-full sm:w-1/2">
              <label className="block text-white text-sm mb-1" htmlFor="state">State</label>
              <input
                className="w-full px-3 py-2 rounded"
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          {/* Address */}
          <div>
            <label className="block text-white text-sm mb-1" htmlFor="address">Address (Optional)</label>
            <input
              className="w-full px-3 py-2 rounded"
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
          <div className="flex justify-center">
            <Button type="submit" text="Proceed to Pay" variant="secondary" size="md" />
          </div>
        </form>
      </div>
    </div>
  );
};

const PaymentSummaryModal = ({
  paymentOption,
  isOpen,
  onBack,
  onPay,
  baseTotal,
  discountedAmount ,
  halfpay,total, // For full: fullDiscountedAmount; for partial: partialDiscountedAmount
}) => {
  if (!isOpen) return null;

  if (paymentOption === "partial") {
    // For partial payment, no extra 10% discount is applied.
    const discountApplied = total-baseTotal
    
    ;
    const halfAmount = halfpay ;
    const partialGST = discountedAmount * 0.18;
    const partialFinalPrice = discountedAmount+partialGST;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary bg-opacity-40 w-full h-full px-4">
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-8 overflow-auto">
          <h2 className="text-center text-3xl font-bold bg-secondary text-white py-4 rounded-t-lg mb-6">
            Order Summary - Partial Payment
          </h2>
          <div className="space-y-6">
            <p className="text-lg">
              <strong>Original Price:</strong>{" "}
              <span className="line-through text-gray-600">
                ₹{total.toLocaleString("en-IN")}
              </span>
            </p>
            <p className="text-lg">
              <strong>Discount Applied:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{discountApplied.toLocaleString("en-IN")}
              </span>
            </p>
            <p className="text-lg">
              <strong>Discounted Price:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{discountedAmount.toLocaleString("en-IN")}
              </span>
            </p>
            <p className="text-lg">
              <strong>Prebook Payment:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{halfAmount.toLocaleString("en-IN")}
              </span>
            </p>
             <p className="text-lg">
              <strong> GST:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{partialGST.toLocaleString("en-IN")}
              </span>
            </p>
            <p className="text-lg">
              <strong>Amount to be paid:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{partialFinalPrice.toLocaleString("en-IN")}
              </span>
            </p>
                       

            <p className="text-center text-2xl font-bold mt-2">
              <strong> Amount Paying now:</strong>{" "}
              <span className="text-primary">
                ₹{halfAmount.toLocaleString("en-IN")}
              </span>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-between mt-8 gap-4">
            <Button onClick={onBack} text="Back" variant="secondary" size="md" />
            <Button onClick={onPay} text="Pay Now" variant="primary" size="md" />
          </div>
        </div>
      </div>
    );
  } else {
    // For full payment, the extra 10% discount applies.
    const discountApplied =((total- baseTotal)+baseTotal*0.05).toFixed(2);
    const fullGST = (discountedAmount*0.95) * 0.18;
    const fullFinalPrice = discountedAmount*0.95 + fullGST;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary bg-opacity-40 w-full h-full px-4">
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl p-8 overflow-auto">
          <h2 className="text-center text-3xl font-bold bg-secondary text-white py-4 rounded-t-lg mb-6">
            Order Summary - Full Payment
          </h2>
          <div className="space-y-4">
            <p className="text-lg">
              <strong>Original Price:</strong>{" "}
              <span className="line-through text-gray-600">
                ₹{total.toLocaleString("en-IN")}
              </span>
            </p>
            <p className="text-lg">
              <strong>Discount Applied:</strong>{" "}
              <span className="text-secondary font-semibold">
                {discountApplied.toLocaleString("en-IN")}
              </span>
            </p>
             <p className="text-lg">
              <strong>GST:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{fullGST.toLocaleString("en-IN")}
              </span>
            </p>
           
            <p className="text-lg">
              <strong>Discounted Price:</strong>{" "}
              <span className="text-secondary font-semibold">
                ₹{(discountedAmount*0.95).toFixed(2).toLocaleString("en-IN")}
              </span>
            </p>
            <div className="text-center">
              <p className="text-2xl font-bold">
                <strong>Final Price:</strong>{" "}
                <span className="text-primary">
                  ₹{fullFinalPrice.toFixed(2).toLocaleString("en-IN")}
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between mt-8 gap-4">
            <Button onClick={onBack} text="Back" variant="secondary" size="md" />
            <Button onClick={onPay} text="Pay Now" variant="primary" size="md" />
          </div>
        </div>
      </div>
    );
  }
};

const PaymentResult = ({ success, email, billDetails, onDownloadBill, onEmailBill, onReturnHome }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-80 px-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 text-center">
        {success ? (
          <>
            <h2 className="text-2xl font-bold text-green-600 mb-4">Payment Successful!</h2>
            <p className="mb-4">
              Your payment was processed successfully. A receipt can be sent to <strong>{email}</strong>.
            </p>
            <p className="mb-6">Download your bill details now</p>
            <div className="space-y-4 mb-6">
              <Button onClick={onDownloadBill} text="Download Bill" variant="secondary" className="mr-3" size="sm" />
              <Button onClick={onEmailBill} text="Email Bill" variant="secondary" size="sm" />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Payment Failed!</h2>
            <p className="mb-6">There was an error processing your payment. Please try again.</p>
          </>
        )}
        <a href="/" className="text-primary font-bold hover:text-secondary underline">Return to Home</a>
      </div>
    </div>
  );
};


const PricingSection = ({ data, validCoupons, dueDate,program }) => {
  const navigate = useNavigate();

  const buttonText = "Reserve Your Spot";
  const [quantities, setQuantities] = useState(data.packages.map(() => 0));
  const [paymentOption, setPaymentOption] = useState("partial");
  const [selectedOffer, setSelectedOffer] = useState("");
  const [specialOfferDiscount, setSpecialOfferDiscount] = useState(0);
  const [couponDiscount, setCouponDiscount] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPackageMessage, setShowPackageMessage] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [invoiceOrderId, setInvoiceOrderId] = useState("");

  // Prepare offers data
  const offersData = data.offers.map((offer) => ({
    label: offer.label,
    discount: offer.discount,
    validUntil: offer.validUntil,
    minQuantity: offer.minQuantity
  }));

  const handleQuantityChange = (index, delta) => {
    setQuantities((prev) => {
      const newQuantities = [...prev];
      newQuantities[index] = Math.max(0, newQuantities[index] + delta);
      return newQuantities;
    });
  };
    const Total = quantities.reduce((sum, qty, index) => {
    const price = parseFloat(String(data.packages[index].standardPrice).replace(/,/g, ""));
    return sum + qty * (isNaN(price) ? 0 : price);
  }, 0);


  const baseTotal = quantities.reduce((sum, qty, index) => {
    const price = parseFloat(String(data.packages[index].discountedPrice).replace(/,/g, ""));
    return sum + qty * (isNaN(price) ? 0 : price);
  }, 0);
const prebook=5000;
const totalQuantity = quantities.reduce((a, b) => a + b, 0);
let halfpay = prebook*totalQuantity;

  const fullDiscountedAmount = baseTotal  * (1 - specialOfferDiscount) ;
  let amountAfterDiscount, gstAmount, finalAmount;
  if (paymentOption === "full") {
    amountAfterDiscount = fullDiscountedAmount*0.95;
    gstAmount = (fullDiscountedAmount*0.95) * 0.18;
    finalAmount = fullDiscountedAmount*0.95 + gstAmount;
  } else {
    // For partial payment, show half-payment info
    amountAfterDiscount = fullDiscountedAmount; // full discounted price without extra 10%
    gstAmount = fullDiscountedAmount* 0.18;
    finalAmount = halfpay;
  }

let rem = ((fullDiscountedAmount+fullDiscountedAmount*0.18) - halfpay) ;
  const openDetailsModal = () => {
    if (baseTotal === 0) {
      setShowPackageMessage(true);
      setTimeout(() => setShowPackageMessage(false), 3000);
      return;
    }
    setShowDetailsModal(true);
  };

  const handleModalSubmit = (formData) => {
    console.log("User Details Submitted:", formData);
    setUserDetails(formData);
    setShowDetailsModal(false);
    setShowSummaryModal(true);
  };

  const handleSummaryBack = () => {
    setShowSummaryModal(false);
    setShowDetailsModal(true);
  };const handleSummaryPayNow = async () => {
    try {
      // Ensure finalAmount is computed before this function call.
      if (!finalAmount) {
        console.error("Final amount not computed.");
        return;
      }
  
      // 1. Create order from backend using finalAmount (in rupees)
      // Note: If your backend expects rupees, you must send finalAmount (not in paise).
      // Make sure you send ALL the fields your backend needs:
const orderResponse = await fetch(`${API_BASE}/order/new`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    name:    userDetails.name,
    email:   userDetails.email,
    phone:   userDetails.mobile,
    amount:  finalAmount.toFixed(2),
  })
});

const { razorpayOrder, orderInDB } = await orderResponse.json();
// now razorpayOrder.id is your new Razorpay order id
const orderId = razorpayOrder.id;
console.log("Order Created, Order ID:", orderId);
  
      // 2. Build invoice details (using your existing logic, ensure it's defined)
      const invoiceItems = data.packages.map((pkg, index) => {
        const qty = quantities[index];
        if (qty > 0) {
          const standardPrice = parseFloat(String(pkg.standardPrice).replace(/,/g, "")) || 0;
          const discountedPrice = parseFloat(String(pkg.discountedPrice).replace(/,/g, "")) || 0;
          return {
            name: pkg.name,
            quantity: qty,
            standardPrice:( standardPrice).toFixed(2),
            discountedPrice: (discountedPrice ).toFixed(2),
          };
        }
        return null;
      }).filter(item => item);
      const overallDiscountedTotal = invoiceItems.reduce(
        (sum, item) => sum + (parseFloat(item.discountedPrice) * item.quantity || 0),
        0
      );      const total = invoiceItems.reduce(
        (sum, item) => sum + (parseFloat(item.standardPrice) * item.quantity || 0),
        0
      );
const dispercentage = (((total-baseTotal)/total)* 100).toFixed(2);
const dispercent = paymentOption === "full" 
  ? (parseFloat(dispercentage) + 5).toFixed(2)
  : dispercentage;

      const fullamount=(fullDiscountedAmount*0.95+((fullDiscountedAmount*0.95)*0.18));
      const invoiceDetails = {
        orderId,
         program:program,

        date: new Date().toLocaleDateString(),
        dueDate: dueDate || new Date(new Date().setDate(new Date().getDate() )).toLocaleDateString(),
        ...userDetails,
        items: invoiceItems,
        overallDiscountedTotal: total.toFixed(2),
        discountApplied:dispercent,
        payableAmount:
          paymentOption === "full"
            ? fullDiscountedAmount*0.95.toFixed(2)
            : (fullDiscountedAmount ).toFixed(2),
        gst:(gstAmount).toFixed(2),
totalPayable:paymentOption === "full"?fullamount.toFixed(2):(fullDiscountedAmount+fullDiscountedAmount*0.18).toFixed(2),
paidAmount: paymentOption === "partial" ? (halfpay).toFixed(2) : (fullamount).toFixed(2),
remainingAmount: paymentOption === "partial" 
  ? (rem).toFixed(2)
  : "0",

      };
  
      // 3. Set up Razorpay payment options using finalAmount (make sure amount is in paise)
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: Math.round(finalAmount * 100), // Convert rupees to paise
        name: "PARAM LIFE WELLNESS LIMITED",
        description: "Payment for your order",
        order_id: orderId,
        handler: async function (response) {
          // response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature
          await fetch(`${API_BASE}/order/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature
            })
          });
          // …
        
          console.log("Payment Verified!");
  
          // Generate the invoice PDF using the invoiceDetails object
          await fetch(`${API_BASE}/generate-invoice`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(invoiceDetails)
          });
          console.log("Invoice generated");
  
          // Update UI accordingly
          setInvoiceOrderId(orderId);
          setPaymentSuccess(true);
          setShowSummaryModal(false);
          setShowPaymentResult(true);
        },
        prefill: {
          name: userDetails.name ,
          email: userDetails.email ,
          contact: userDetails.mobile
        },
        theme: { color: "#F58020" }
      };
  
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert("Payment initiation failed. Please try again.");
      setPaymentSuccess(false);
      setShowSummaryModal(false);
      setShowPaymentResult(true);
    }
  };
  
  const handleApplyOffer = () => {
    const idx = parseInt(selectedOffer, 10);
    if (isNaN(idx) || !offersData[idx]) {
      setSpecialOfferDiscount(0);
      return;
    }
    const offer = offersData[idx];

    if (offer.validUntil && new Date() > new Date(offer.validUntil)) {
      alert("Offer expired. Please select another offer.");
      setSpecialOfferDiscount(0);
      return;
    }

    if (offer.minQuantity) {
      const totalQty = quantities.reduce((a, b) => a + b, 0);
      if (totalQty < offer.minQuantity) {
        alert(`Booking for at least ${offer.minQuantity} people is required.`);
        setSpecialOfferDiscount(0);
        return;
      }
    }

    setSpecialOfferDiscount(offer.discount);
    alert(`Offer Applied: ${(offer.discount * 100).toFixed(0)}% off`);
  };


  const handleDownloadBill = () => {
    if (!invoiceOrderId) {
      alert("Invoice not generated yet.");
      return;
    }
    const invoiceUrl = `${API_BASE}/invoices/invoice_${invoiceOrderId}.pdf`;
    window.open(invoiceUrl, "_blank");
  };

  const handleEmailBill = async () => { 
    if (!invoiceOrderId) {
      alert("Invoice not generated yet.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/email-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: invoiceOrderId,
          email: userDetails.email,    // Customer email from DetailsModal
          name: userDetails.name,      // Customer name from DetailsModal
          programName: program         // Dynamic program name passed as a prop
        }),
      });
      const data = await response.json();
      if (response.ok) {
        alert("Invoice emailed successfully!");
      } else {
        alert("Failed to email invoice: " + data.error);
      }
    } catch (error) {
      console.error("Error emailing invoice:", error);
      alert("Error emailing invoice. Please try again.");
    }
  };
      
  const handleReturnHome = () => {
    window.location.href = "/";
  };

  return (
    <section className="py-4 md:py-6 bg-light flex justify-center px-4">
      <div className="w-full p-4 md:p-6 bg-white shadow-lg rounded-2xl border border-gray-200 max-w-full sm:max-w-xl md:max-w-2xl">
        <h2 className="text-2xl font-bold text-[#578d48] text-center mb-4">Choose your package:</h2>
        <div className="bg-primary text-white p-4 rounded-xl">
          <p className="text-center text-sm mb-3 mt-2"> Reserve your spot now .</p>
          <ul className="space-y-3">
            {data.packages.map((pkg, index) => (
              <li key={index} className="flex items-center justify-between border-b pb-3">
                <div className="flex-1">
                  <span className="font-bold text-yellow-300 text-md block">{pkg.name}</span>
                  <div className="flex gap-1 mt-1">
                    <span className="text-sm text-gray-600">
                      <span className="line-through">₹{pkg.standardPrice}</span>
                    </span>
                    <span className="text-md text-white font-bold">₹{pkg.discountedPrice}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleQuantityChange(index, -1)}
                    className="w-7 h-7 rounded-full bg-white text-primary border border-primary flex items-center justify-center text-xs"
                  >
                    -
                  </button>
                  <span className="text-xs font-bold">{quantities[index]}</span>
                  <button
                    onClick={() => handleQuantityChange(index, 1)}
                    className="w-7 h-7 rounded-full bg-white text-primary border border-primary flex items-center justify-center text-xs"
                  >
                    +
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="text-right font-bold text-white mt-4">
            Subtotal: ₹{Total.toLocaleString("en-IN")}
          </div>
          {showPackageMessage && (
            <div className="text-center text-red text-sm mt-2">
              Please select a package.
            </div>
          )}
          <div className="text-center text-white text-sm mt-2">
            Pay remaining before the retreat starts
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center mt-4 gap-4 text-gray-800 text-sm">
{/* Payment Option: Prebook */}
<div className="flex items-center mt-4">
  <input
    type="radio"
    id="partial"
    name="paymentOption"
    value="partial"
    checked={paymentOption === "partial"}
    onChange={() => setPaymentOption("partial")}
    className="mr-2"
  />
  <label htmlFor="partial" className="cursor-pointer">Prebook @₹5000</label>
</div>

{/* Only show this input if Prebook is selected */}
            <div className="flex items-center mt-4">
              <input
                type="radio"
                id="full"
                name="paymentOption"
                value="full"
                checked={paymentOption === "full"}
                onChange={() => setPaymentOption("full")}
                className="mr-2"
              />
              <label htmlFor="full" className="cursor-pointer">Pay in Full </label>
            </div>
          </div>
          
          {/* Pricing details: Amount after discount, GST and Final Amount */}
          <div className="mt-4">
            {paymentOption === "full" ? (
              <>
                <div className="text-right font-bold text-white">
                  Amount after discount: ₹{fullDiscountedAmount.toLocaleString("en-IN")}
                </div>
                                <div className="text-right font-bold text-white mt-2">
                  Amount after 5% discount: ₹{fullDiscountedAmount*0.95.toLocaleString("en-IN")}
                </div>

                <div className="text-right font-bold text-white mt-2">
                  18% GST Amount: ₹{((fullDiscountedAmount*0.95) * 0.18).toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  Final Payable Amount: ₹{(fullDiscountedAmount*0.95 + ((fullDiscountedAmount*0.95) * 0.18)).toFixed(2).toLocaleString("en-IN")}
                </div>
              </>
            ) : (
              <>
                <div className="text-right font-bold text-white">
                  Amount after discount: ₹{fullDiscountedAmount.toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  18% GST Amount : ₹{((fullDiscountedAmount ) * 0.18).toLocaleString("en-IN")}
                </div>
                <div className="text-right font-bold text-white mt-2">
                  Current Payable Amount: ₹{(halfpay).toLocaleString("en-IN")}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-center mt-4">
          <Button onClick={openDetailsModal} text={buttonText} variant="primary" size="md" />
        </div>
      </div>
      <DetailsModal 
        isOpen={showDetailsModal} 
        onClose={() => setShowDetailsModal(false)} 
        onSubmit={handleModalSubmit} 
      />
      <PaymentSummaryModal
        paymentOption={paymentOption}
        isOpen={showSummaryModal}
        onBack={handleSummaryBack}
        onPay={handleSummaryPayNow}
        baseTotal={baseTotal}
        total={Total}
        discountedAmount={fullDiscountedAmount}
        halfpay={halfpay}
      />
      {showPaymentResult && (
navigate("/thank-you-page", {
  state: {
    success: paymentSuccess,
    email: userDetails.email,
    invoiceOrderId: invoiceOrderId,
    userDetails: userDetails,
    program: program
  }
})
)}
      
    </section>
  );
};

export default PricingSection;
